import React from 'react';
import { useInput, InputProps } from 'react-admin';
import { Box, FormControl, FormHelperText, TextField } from '@mui/material';

const HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/;

/**
 * An empty value is valid and means "unset" — the client falls back to its own default
 * background. Only a non-empty value has to be a 6-digit hex colour.
 */
export const validateHexColor = (value?: string | null): string | undefined =>
  !value || HEX_PATTERN.test(value) ? undefined : 'Use a 6-digit hex colour, e.g. #AABBCC';

interface ColorInputProps extends Omit<InputProps, 'source'> {
  source: string;
  label?: string;
  helperText?: string;
}

/**
 * Colour picker for a hex string. react-admin ships no colour input and the app has no
 * colour-picker dependency, so this is built on `useInput` in the same shape as
 * FirebaseImageInput / TitlestyleInput: a native swatch for picking plus a text field so
 * a known brand hex can be pasted in directly.
 */
const ColorInput: React.FC<ColorInputProps> = ({ source, label = 'Colour', helperText, ...rest }) => {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useInput({ source, validate: rest.validate ?? validateHexColor, ...rest });

  // <input type="color"> cannot represent "no colour", so it shows a neutral swatch while
  // the stored value stays empty until the admin actually picks something.
  const swatch = HEX_PATTERN.test(value ?? '') ? (value as string) : '#000000';

  return (
    <FormControl fullWidth margin="normal" error={!!error}>
      <Box display="flex" alignItems="center" gap={2}>
        <input
          type="color"
          aria-label={`${label} picker`}
          value={swatch}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          style={{
            width: 56,
            height: 56,
            padding: 0,
            border: '1px solid rgba(0,0,0,0.23)',
            borderRadius: 4,
            cursor: 'pointer',
            background: 'none',
            flexShrink: 0,
          }}
        />
        <TextField
          label={label}
          value={value ?? ''}
          placeholder="#AABBCC"
          error={!!error}
          fullWidth
          onChange={(e) => {
            const next = e.target.value.trim();
            // Empty clears the colour rather than writing an invalid string.
            onChange(next === '' ? '' : next.toUpperCase());
          }}
        />
      </Box>
      <FormHelperText>
        {error?.message ? String(error.message) : helperText}
      </FormHelperText>
    </FormControl>
  );
};

export default ColorInput;
